import { XIcon } from '@phosphor-icons/react'
import { Dialog as D } from 'radix-ui'
import { useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react'
import { Button, IconButton } from './Button.tsx'
import { Textarea } from './Field.tsx'

export interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  footNote?: ReactNode
  width?: number
  /** Element to focus first (defaults to the first focusable control). */
  initialFocus?: () => HTMLElement | null
  /** false: no close button, and Escape or a click outside does nothing (work that must finish first). */
  dismissible?: boolean
}

export function Dialog({ open, onOpenChange, title, description, children, footer, footNote, width, initialFocus, dismissible = true }: DialogProps) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="rt-scrim" />
        <D.Content
          className="rt-dialog"
          style={width ? ({ '--dialog-w': `${width}px` } as CSSProperties) : undefined}
          onOpenAutoFocus={(e) => {
            const el = initialFocus?.()
            if (el) {
              e.preventDefault()
              el.focus()
            }
          }}
          onEscapeKeyDown={dismissible ? undefined : (e) => e.preventDefault()}
          onPointerDownOutside={dismissible ? undefined : (e) => e.preventDefault()}
          onInteractOutside={dismissible ? undefined : (e) => e.preventDefault()}
        >
          <div className="rt-dialog__head">
            <div className="rt-dialog__titles">
              <D.Title className="rt-dialog__title">{title}</D.Title>
              {description ? <D.Description className="rt-dialog__desc">{description}</D.Description> : <D.Description className="rt-visually-hidden">{title}</D.Description>}
            </div>
            {dismissible ? (
              <D.Close asChild>
                <IconButton label="Close" icon={<XIcon />} size="sm" tooltip={false} />
              </D.Close>
            ) : null}
          </div>
          {children ? <div className="rt-dialog__body">{children}</div> : null}
          {footer ? (
            <div className="rt-dialog__foot">
              {footNote ? <span className="rt-dialog__foot-note">{footNote}</span> : null}
              {footer}
            </div>
          ) : null}
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}

export interface SheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  side?: 'right' | 'left'
  width?: number
  /** Hide the visual header (e.g. a navigation rail rendered inside). Title stays for screen readers. */
  bare?: boolean
}

export function Sheet({ open, onOpenChange, title, description, children, footer, side = 'right', width, bare }: SheetProps) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="rt-scrim" />
        <D.Content
          className="rt-sheet"
          data-side={side}
          style={width ? ({ '--sheet-w': `${width}px` } as CSSProperties) : undefined}
          tabIndex={-1}
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            ;(e.currentTarget as HTMLElement | null)?.focus()
          }}
        >
          {bare ? (
            <>
              <D.Title className="rt-visually-hidden">{title}</D.Title>
              <D.Description className="rt-visually-hidden">{description ?? title}</D.Description>
              {children}
            </>
          ) : (
            <>
              <div className="rt-sheet__head">
                <div className="rt-dialog__titles">
                  <D.Title className="rt-dialog__title">{title}</D.Title>
                  {description ? <D.Description className="rt-dialog__desc">{description}</D.Description> : <D.Description className="rt-visually-hidden">{title}</D.Description>}
                </div>
                <D.Close asChild>
                  <IconButton label="Close" icon={<XIcon />} size="sm" tooltip={false} />
                </D.Close>
              </div>
              <div className="rt-sheet__body">{children}</div>
              {footer ? <div className="rt-sheet__foot">{footer}</div> : null}
            </>
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  /** Extra content (e.g. a summary of what changes). */
  children?: ReactNode
  confirmLabel: string
  /** `danger` for destructive changes. */
  tone?: 'primary' | 'danger'
  /** Ask for a written reason (audited). Minimum length enforced client-side to match the API. */
  requireReason?: boolean
  reasonLabel?: string
  reasonPlaceholder?: string
  minReason?: number
  onConfirm: (reason: string) => Promise<unknown> | unknown
  /** Error to show (e.g. the API's problem detail). */
  error?: ReactNode
}

/** Confirmation for consequential actions. With `requireReason`, the reason is part of the audit record. */
export function ConfirmDialog({
  open, onOpenChange, title, description, children, confirmLabel, tone = 'primary', requireReason, reasonLabel = 'Reason',
  reasonPlaceholder = 'Why are you making this change? It is recorded in the audit log.', minReason = 3, onConfirm, error,
}: ConfirmDialogProps) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [touched, setTouched] = useState(false)
  const formId = useId()
  useEffect(() => {
    if (open) {
      setReason('')
      setTouched(false)
      setBusy(false)
    }
  }, [open])
  const tooShort = requireReason && reason.trim().length < minReason
  const submit = async () => {
    setTouched(true)
    if (tooShort) return
    setBusy(true)
    try {
      await onConfirm(reason.trim())
    } catch {
      /* the caller shows the failure through `error` */
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      footNote={requireReason ? 'Recorded in the audit log with your name.' : undefined}
      footer={
        <>
          <Button variant="quiet" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} type="submit" form={formId} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        {children}
        {requireReason ? (
          <Textarea
            label={reasonLabel}
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={reasonPlaceholder}
            maxLength={500}
            rows={3}
            autoFocus
            error={touched && tooShort ? `Write at least ${minReason} characters.` : undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                void submit()
              }
            }}
          />
        ) : null}
        {error ? (
          <p className="rt-field__error" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  )
}
